import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  directoryConfig,
  directoryPageId,
  hydrateDirectoryPage,
  sortDirectoryItems,
} from "./commonDirectory";

const CARGO_CHUNK_TARGET_BYTES = 700000;
const CARGO_CODE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const CARGO_CODE_LENGTH = 12;
const EMPTY_FORM = {
  contactRole: "RECIPIENT",
  roleChanged: false,
  cargoName: "",
  quantity: "1",
  unit: "件",
  note: "",
  sender: { name: "", company: "", email: "", phone: "", addressText: "" },
  recipient: { name: "", company: "", email: "", phone: "", addressText: "" },
};

const PAGE_STYLES = `
.dmc-page{--dmc-ink:#17212f;--dmc-muted:#6b7788;--dmc-line:#e7ebf0;--dmc-orange:#f1842b;--dmc-orange-soft:#fff4e8;min-height:100%;box-sizing:border-box;padding:16px 18px 28px;background:#f5f6f8;color:var(--dmc-ink);font-family:inherit}
.dmc-page *{box-sizing:border-box}
.dmc-shell{max-width:1440px;margin:0 auto}
.dmc-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;margin-bottom:24px}
.dmc-heading h1{margin:0;font-size:26px;line-height:1.25;font-weight:800;letter-spacing:.02em}
.dmc-heading p{margin:8px 0 0;color:var(--dmc-muted);font-size:14px;line-height:1.65}
.dmc-heading-actions{display:flex;gap:10px;flex-shrink:0}
.dmc-work-review-actions{align-items:center;justify-content:space-between;width:100%}
.dmc-button{border:1px solid #e1e6ec;border-radius:11px;background:#fff;color:#344254;min-height:42px;padding:0 15px;font:inherit;font-size:14px;font-weight:700;cursor:pointer;transition:transform .15s ease,box-shadow .15s ease,border-color .15s ease}
.dmc-button:hover{border-color:#f2b078;box-shadow:0 5px 16px rgba(28,39,54,.07);transform:translateY(-1px)}
.dmc-button:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none}
.dmc-button-primary{border-color:var(--dmc-orange);background:var(--dmc-orange);color:#fff;box-shadow:0 8px 18px rgba(241,132,43,.18)}
.dmc-button-primary:hover{background:#e8761e;border-color:#e8761e}
.dmc-button-quiet{background:#fff}
.dmc-kpis{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-bottom:20px}
.dmc-kpi{border:1px solid var(--dmc-line);border-radius:15px;background:#fff;padding:18px 20px;box-shadow:0 4px 14px rgba(20,32,48,.025)}
.dmc-kpi-label{color:var(--dmc-muted);font-size:13px;font-weight:650}
.dmc-kpi-value{margin-top:7px;font-size:26px;line-height:1.1;font-weight:800;font-variant-numeric:tabular-nums}
.dmc-kpi-foot{margin-top:6px;color:#929cab;font-size:12px}
.dmc-toolbar{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:14px 16px;border:1px solid var(--dmc-line);border-radius:15px 15px 0 0;background:#fff}
.dmc-search-wrap{position:relative;flex:1;max-width:520px}
.dmc-search-icon{position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#97a2b1;font-size:18px;pointer-events:none}
.dmc-search{width:100%;height:42px;border:1px solid #e2e7ed;border-radius:10px;background:#fbfcfd;padding:0 13px 0 40px;color:var(--dmc-ink);font:inherit;font-size:14px;outline:none}
.dmc-search:focus,.dmc-input:focus,.dmc-select:focus,.dmc-textarea:focus{border-color:#f3a15d;box-shadow:0 0 0 3px rgba(241,132,43,.12)}
.dmc-result-count{display:flex;align-items:center;justify-content:space-between;gap:10px;color:var(--dmc-muted);font-size:13px;white-space:nowrap}
.dmc-select-all{min-height:34px;border:1px solid #e1e6ec;border-radius:9px;background:#fff;color:#526173;padding:0 11px;font:inherit;font-size:12px;font-weight:750;cursor:pointer;transition:border-color .15s ease,color .15s ease,background .15s ease}
.dmc-select-all:hover{border-color:#f2b078;background:#fffaf6;color:#b85d1d}
.dmc-select-all:disabled{opacity:.5;cursor:not-allowed}
.dmc-toolbar-actions{display:flex;align-items:center;justify-content:flex-end;gap:8px;flex-wrap:wrap}
.dmc-delete-assigned{min-height:34px;border:1px solid #f0d1ce;border-radius:9px;background:#fff8f7;color:#b64b43;padding:0 11px;font:inherit;font-size:12px;font-weight:750;white-space:nowrap;cursor:pointer;transition:background .15s ease,border-color .15s ease}
.dmc-delete-assigned:hover:not(:disabled){border-color:#e7aaa5;background:#fff0ee;color:#a53c35}
.dmc-delete-assigned:disabled{opacity:.5;cursor:not-allowed}
.dmc-content{border:1px solid var(--dmc-line);border-top:0;border-radius:0 0 15px 15px;background:#fff;min-height:240px;overflow:hidden}
.dmc-list{display:flex;flex-direction:column}
.dmc-record{padding:18px 20px;border-top:1px solid #edf0f3}
.dmc-record.is-assigned{background:#fffafa}
.dmc-record.is-assigned .dmc-status{background:#fff0ee;color:#b64b43}
.dmc-record:first-child{border-top:0}
.dmc-record-head{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-bottom:14px}
.dmc-record-title{display:flex;align-items:center;gap:11px;min-width:0}
.dmc-name-wrap{min-width:0}
.dmc-cargo-name{font-size:16px;font-weight:800;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dmc-code{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:3px;color:#748092;font-size:12px;font-weight:700;letter-spacing:.08em;font-variant-numeric:tabular-nums}
.dmc-code-updated{display:inline-flex;align-items:center;padding:2px 7px;border-radius:999px;background:#eaf7ef;color:#27824b;font-size:10px;font-weight:800;letter-spacing:0;white-space:nowrap}
.dmc-head-right{display:flex;align-items:center;gap:12px;flex-shrink:0}
.dmc-quantity{padding:7px 10px;border-radius:9px;background:#f5f7f9;color:#344254;font-size:13px;font-weight:750;white-space:nowrap}
.dmc-status{padding:6px 10px;border-radius:999px;background:#eaf7ef;color:#27824b;font-size:12px;font-weight:750;white-space:nowrap}
.dmc-actions{display:flex;align-items:center;gap:6px}
.dmc-action{border:0;background:transparent;color:#667386;padding:7px 9px;border-radius:8px;font:inherit;font-size:13px;font-weight:700;cursor:pointer}
.dmc-action:hover{background:#f4f6f8;color:#182333}
.dmc-action-danger:hover{background:#fff1f0;color:#c53a32}
.dmc-party-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-left:29px}
.dmc-party{min-width:0;padding:12px 14px;border:1px solid #edf0f3;border-radius:11px;background:#fcfcfd}
.dmc-party-label{display:flex;align-items:center;gap:7px;margin-bottom:7px;color:#6a7788;font-size:12px;font-weight:750}
.dmc-party-dot{width:7px;height:7px;border-radius:50%;background:#48b47b}
.dmc-party-recipient .dmc-party-dot{background:#e99138}
.dmc-party-name{font-size:14px;font-weight:750;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dmc-party-meta{margin-top:4px;color:#768294;font-size:12px;line-height:1.55;overflow-wrap:anywhere}
.dmc-party-address{margin-top:5px;color:#425064;font-size:12px;line-height:1.55;overflow-wrap:anywhere}
.dmc-note{margin:10px 0 0 29px;color:#7b8796;font-size:12px;line-height:1.55;overflow-wrap:anywhere}
.dmc-empty,.dmc-loading,.dmc-error{padding:52px 20px;text-align:center;color:var(--dmc-muted);font-size:14px}
.dmc-empty-icon{width:54px;height:54px;margin:0 auto 12px;display:grid;place-items:center;border-radius:16px;background:#fff4e8;color:#df741f;font-size:25px}
.dmc-empty strong{display:block;margin-bottom:7px;color:#344254;font-size:16px}
.dmc-error{color:#b42318}
.dmc-notice{margin:0 0 16px;padding:11px 14px;border:1px solid #b7e2c5;border-radius:10px;background:#effaf2;color:#247344;font-size:13px;font-weight:650}
.dmc-error-banner{margin:0 0 16px;padding:11px 14px;border:1px solid #f1c7c3;border-radius:10px;background:#fff4f2;color:#a33229;font-size:13px}
.dmc-error-banner button{margin-left:10px;border:0;background:transparent;color:inherit;font:inherit;font-weight:800;text-decoration:underline;cursor:pointer}
.dmc-select-checkbox{width:18px;height:18px;margin:0 1px 0 0;accent-color:#f1842b;cursor:pointer;flex:none}
.dmc-selection-bar{display:flex;align-items:center;justify-content:space-between;gap:14px;margin:0 0 14px;padding:12px 15px;border:1px solid #f4d1b4;border-radius:12px;background:#fff8f1;color:#70421f}
.dmc-selection-bar strong{font-size:13px}.dmc-selection-bar small{display:block;margin-top:3px;color:#9a7659;font-size:11px}
.dmc-selection-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.dmc-work-modal{width:min(980px,100%)}
.dmc-preview-stations{display:grid;gap:12px}
.dmc-preview-station{padding:14px;border:1px solid #e7ebf0;border-radius:12px;background:#fff}
.dmc-preview-station-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:11px}
.dmc-preview-station-title{display:flex;align-items:center;gap:9px;min-width:0}
.dmc-preview-station-title strong{color:#263548;font-size:14px}.dmc-preview-station-title small{display:block;margin-top:2px;color:#8290a0;font-size:11px}
.dmc-preview-address{display:grid;grid-template-columns:78px minmax(0,1fr);align-items:center;gap:8px;margin-bottom:11px;color:#667486;font-size:12px;font-weight:700}
.dmc-preview-contacts{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
.dmc-preview-contact{min-width:0;padding:10px 11px;border:1px solid #edf0f3;border-radius:10px;background:#fafbfc}
.dmc-preview-contact strong{display:block;color:#344254;font-size:12px}.dmc-preview-contact>span{display:block;margin-top:3px;color:#7d8997;font-size:11px;overflow-wrap:anywhere}
.dmc-preview-contact ul{display:grid;gap:4px;margin:8px 0 0;padding-left:17px;color:#526173;font-size:11px}
.dmc-work-plan-error{margin:12px 0 0;padding:10px 12px;border:1px solid #f1c7c3;border-radius:9px;background:#fff4f2;color:#a33229;font-size:12px;line-height:1.5}
@media(max-width:640px){.dmc-work-modal .dmc-modal-foot{align-items:stretch;flex-direction:column}.dmc-work-modal .dmc-modal-foot .dmc-heading-actions{display:grid;width:100%;grid-template-columns:1fr 1fr}.dmc-work-modal .dmc-modal-foot .dmc-button-primary{grid-column:1/-1}.dmc-work-modal .dmc-modal-foot .dmc-work-review-actions{display:flex;align-items:center;justify-content:space-between;width:100%}.dmc-work-modal .dmc-modal-foot .dmc-work-review-actions .dmc-button-primary{grid-column:auto}}
.dmc-modal-backdrop{position:fixed;z-index:5000;inset:0;display:flex;align-items:center;justify-content:center;padding:10px;background:rgba(17,24,39,.48);backdrop-filter:blur(3px)}
.dmc-modal{width:min(760px,100%);max-height:min(92vh,920px);display:flex;flex-direction:column;overflow:hidden;border:1px solid rgba(255,255,255,.7);border-radius:18px;background:#fff;box-shadow:0 24px 70px rgba(15,23,42,.24)}
.dmc-modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;padding:14px 16px 12px;border-bottom:1px solid #edf0f3}
.dmc-modal-head h2{margin:0;font-size:20px;font-weight:800}
.dmc-modal-head p{margin:6px 0 0;color:var(--dmc-muted);font-size:13px;line-height:1.5}
.dmc-modal-close{width:36px;height:36px;border:0;border-radius:10px;background:#f4f6f8;color:#647184;font-size:22px;cursor:pointer}
.dmc-modal-body{flex:1 1 auto;min-height:0;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;scrollbar-gutter:stable;padding:12px 16px 16px}
.dmc-form-section{margin-bottom:20px;padding:14px;border:1px solid #e9edf1;border-radius:13px;background:#fff}
.dmc-form-section:last-child{margin-bottom:0}
.dmc-section-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:13px}
.dmc-section-head strong{font-size:14px;font-weight:800}
.dmc-section-caption{color:#8792a1;font-size:12px}

.dmc-party-grid-single{grid-template-columns:minmax(0,1fr);max-width:680px}
.dmc-contact-section-head>div{min-width:0}
.dmc-contact-picker-button{flex:none;min-height:36px;border:1px solid #f0c39f;border-radius:9px;background:#fff8f1;color:#a6541c;padding:0 12px;font:inherit;font-size:12px;font-weight:750;cursor:pointer}
.dmc-contact-picker-button:hover{border-color:#e9a46d;background:#fff2e4}
.dmc-contact-picker-button:disabled{opacity:.55;cursor:not-allowed}
.dmc-role-toggle{display:inline-flex;align-items:center;gap:4px;margin:0 0 8px;padding:4px;border:1px solid #e7ebf0;border-radius:999px;background:#f4f6f8}
.dmc-role-option{min-width:92px;min-height:34px;border:0;border-radius:999px;background:transparent;color:#667386;padding:0 15px;font:inherit;font-size:13px;font-weight:750;cursor:pointer;transition:background .15s ease,color .15s ease,box-shadow .15s ease}
.dmc-role-option.is-active{background:#fff;color:#b85d1d;box-shadow:0 2px 7px rgba(30,41,59,.1)}
.dmc-role-option:focus-visible,.dmc-contact-picker-button:focus-visible{outline:3px solid rgba(241,132,43,.25);outline-offset:2px}
.dmc-role-description{margin:0 0 12px;color:#7b8796;font-size:12px;line-height:1.5}
.dmc-contact-picker-backdrop{z-index:5100}
.dmc-contact-picker{width:min(560px,100%);max-height:min(78vh,720px)}
.dmc-contact-search{display:flex;align-items:center;gap:9px;margin:12px 16px;padding:0 12px;border:1px solid #e1e6ec;border-radius:10px;background:#fbfcfd;color:#98a2b0}
.dmc-contact-search input{width:100%;height:42px;border:0;outline:0;background:transparent;color:#1d2939;font:inherit;font-size:14px}
.dmc-contact-list{overflow:auto;max-height:calc(min(78vh,720px) - 160px);padding:0 16px 16px}
.dmc-contact-item{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;min-height:64px;margin:0 0 7px;padding:11px 12px;border:1px solid #e9edf1;border-radius:11px;background:#fff;text-align:left;cursor:pointer}
.dmc-contact-item:hover{border-color:#efbd96;background:#fffaf6}
.dmc-contact-item-main{display:block;min-width:0}
.dmc-contact-item-main strong{display:block;color:#263548;font-size:14px;font-weight:750;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dmc-contact-item-main small{display:block;margin-top:4px;color:#7b8796;font-size:11px;line-height:1.45;overflow-wrap:anywhere}
.dmc-contact-item-action{flex:none;color:#b85d1d;font-size:12px;font-weight:750}
.dmc-contact-empty{padding:30px 18px;text-align:center;color:#7b8796;font-size:13px;line-height:1.55}
.dmc-contact-empty.is-error{color:#a33229}
.dmc-contact-empty .dmc-action{display:block;margin:8px auto 0}
.dmc-contact-load-more{display:block;width:100%;min-height:40px;border:1px dashed #d7dee6;border-radius:9px;background:#fafbfc;color:#627083;font:inherit;font-size:12px;font-weight:700;cursor:pointer}
.dmc-contact-load-more:disabled{opacity:.55;cursor:wait}
.dmc-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.dmc-field{display:flex;flex-direction:column;gap:6px;min-width:0}
.dmc-field-wide{grid-column:1/-1}
.dmc-field label{color:#596679;font-size:12px;font-weight:700}
.dmc-required{color:#d94b3d}
.dmc-input,.dmc-select,.dmc-textarea{width:100%;min-width:0;border:1px solid #e0e5eb;border-radius:9px;background:#fff;padding:10px 11px;color:#1d2939;font:inherit;font-size:14px;outline:none;transition:border-color .15s,box-shadow .15s}
.dmc-input,.dmc-select{height:42px}
.dmc-textarea{min-height:72px;resize:vertical;line-height:1.5}
.dmc-inline-fields{display:grid;grid-template-columns:minmax(0,1fr) 145px;gap:10px}
.dmc-form-error{margin:0 16px;color:#b42318;font-size:13px}
.dmc-modal-foot{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:12px 16px calc(12px + env(safe-area-inset-bottom,0px));border-top:1px solid #edf0f3;background:#fbfcfd}
.dmc-foot-hint{color:#8792a1;font-size:12px;line-height:1.5}
@media(max-width:900px){.dmc-page{padding:14px 12px 20px}.dmc-party-grid{margin-left:0}.dmc-note{margin-left:0}}
@media(max-width:640px){.dmc-page{padding:12px 10px 20px}.dmc-heading{align-items:stretch;flex-direction:column;margin-bottom:18px}.dmc-heading-actions{width:100%}.dmc-heading-actions .dmc-button{flex:1}.dmc-heading h1{font-size:24px}.dmc-kpis{gap:8px}.dmc-kpi{padding:13px 12px;border-radius:12px}.dmc-kpi-label{font-size:11px}.dmc-kpi-value{font-size:21px}.dmc-kpi-foot{font-size:10px}.dmc-toolbar{align-items:stretch;flex-direction:column;padding:11px}.dmc-search-wrap{max-width:none}.dmc-result-count{text-align:right}.dmc-selection-bar{align-items:stretch;flex-direction:column}.dmc-selection-actions{display:grid;grid-template-columns:1fr 1fr}.dmc-selection-actions .dmc-button{min-height:40px;padding:0 8px;font-size:12px}.dmc-record{padding:15px 12px}.dmc-record-head{align-items:flex-start;gap:8px}.dmc-record-title{gap:8px}.dmc-cargo-name{max-width:45vw;font-size:15px}.dmc-head-right{align-items:flex-end;flex-direction:column;gap:6px}.dmc-quantity{padding:6px 8px;font-size:12px}.dmc-actions{gap:0}.dmc-action{padding:5px 7px;font-size:12px}.dmc-party-grid{grid-template-columns:1fr;gap:8px}.dmc-party{padding:10px 11px}.dmc-preview-station-head{align-items:flex-start;flex-direction:column}.dmc-preview-address{grid-template-columns:1fr;gap:5px}.dmc-preview-contacts{grid-template-columns:1fr}.dmc-modal-backdrop{align-items:flex-end;padding:0}.dmc-modal{width:100%;max-height:94vh;border-radius:18px 18px 0 0}.dmc-work-modal{width:100%}.dmc-modal-head{padding:13px 14px 10px}.dmc-modal-body{padding:10px 12px 14px}.dmc-fields{grid-template-columns:1fr;gap:10px}.dmc-field-wide{grid-column:auto}.dmc-inline-fields{grid-template-columns:minmax(0,1fr) 110px}.dmc-modal-foot{padding:10px 12px calc(10px + env(safe-area-inset-bottom,0px))}.dmc-form-section{padding:11px}}

@media(max-width:640px){.dmc-contact-section-head{align-items:flex-start;flex-direction:column}.dmc-contact-picker-button{width:100%}.dmc-contact-picker{width:100%;max-height:88vh;border-radius:18px 18px 0 0}.dmc-contact-search{margin-left:12px;margin-right:12px}.dmc-contact-list{max-height:calc(88vh - 155px);padding-left:12px;padding-right:12px}}
@media(max-width:640px){.dmc-result-count{width:100%;align-items:center;justify-content:space-between;gap:8px;white-space:normal;text-align:left}.dmc-toolbar-actions{gap:6px}.dmc-delete-assigned,.dmc-select-all{padding:0 8px;font-size:11px}}
/* Match the chat page frame while reclaiming Home's 22px content inset. */
.dmc-page{width:auto;min-height:100%;margin:-22px;padding:22px;border:1px solid rgba(148,163,184,.12);border-radius:24px;background:#fff;box-shadow:0 12px 28px rgba(15,23,42,.05)}
@media(max-width:760px){.dmc-page{padding:18px}}
@media(max-width:480px){.dmc-page{padding:14px}}
`;

function text(value) {
  return String(value ?? "").trim();
}

function emptyParty() {
  return { name: "", company: "", email: "", phone: "", addressText: "" };
}

function hasPartyData(party) {
  return [party?.name, party?.company, party?.email, party?.phone, party?.addressText]
    .some((value) => Boolean(text(value)));
}

function cargoContactRole(record) {
  const role = text(record?.contactRole).toUpperCase();
  if (role === "SENDER" || role === "RECIPIENT") return role;
  const hasSender = hasPartyData(record?.sender);
  const hasRecipient = hasPartyData(record?.recipient);
  if (hasSender && hasRecipient) return "BOTH";
  if (hasRecipient) return "RECIPIENT";
  return "SENDER";
}

export function makeCargoCode() {
  return Array.from(
    { length: CARGO_CODE_LENGTH },
    () => CARGO_CODE_CHARS[Math.floor(Math.random() * CARGO_CODE_CHARS.length)],
  ).join("");
}

function cargoMetaRef(orgId) {
  return doc(db, "orgs", orgId, "cargoInventory", "meta");
}

function cargoChunkRef(orgId, chunkId) {
  return doc(db, "orgs", orgId, "cargoInventory", "meta", "chunks", chunkId);
}

function cargoCodeRef(orgId, cargoId) {
  return doc(db, "orgs", orgId, "cargoInventory", "meta", "codes", cargoId);
}

function cargoChunkId(sequence) {
  return `chunk_${String(sequence).padStart(6, "0")}`;
}

async function reserveCargoChunkId(transaction, orgId, startingSequence) {
  let sequence = Math.max(1, Number(startingSequence) || 1);
  for (let attempts = 0; attempts < 1000; attempts += 1) {
    const chunkId = cargoChunkId(sequence++);
    const snapshot = await transaction.get(cargoChunkRef(orgId, chunkId));
    if (!snapshot.exists()) return { chunkId, nextSequence: sequence };
  }
  throw new Error("無法取得新的貨物分片編號，請重新整理後再試。");
}

function sequenceFromChunkId(chunkId) {
  const match = String(chunkId || "").match(/chunk_(\d+)/i);
  return match ? Number(match[1]) : 0;
}

function nextChunkSequence(meta, chunkIds) {
  const highestExisting = chunkIds.reduce(
    (highest, id) => Math.max(highest, sequenceFromChunkId(id)),
    0,
  );
  return Math.max(Number(meta?.nextChunkSequence) || 1, highestExisting + 1);
}

function estimateItemsBytes(items) {
  const json = JSON.stringify(items, (key, value) => {
    if (key === "createdAt" || key === "updatedAt") return null;
    return value;
  });
  return new TextEncoder().encode(json).length;
}

function buildCargoRecord(form, existing, user, existingCargoIds = []) {
  const existingIds = new Set(existingCargoIds);
  const contactRole = text(form?.contactRole).toUpperCase() === "RECIPIENT"
    ? "RECIPIENT"
    : "SENDER";
  const preserveLegacyBoth = Boolean(
    existing && cargoContactRole(existing) === "BOTH" && !form?.roleChanged,
  );
  let cargoId = text(existing?.cargoId);
  if (!cargoId) {
    do {
      cargoId = makeCargoCode();
    } while (existingIds.has(cargoId));
  }
  const existingData = { ...(existing || {}) };
  delete existingData._chunkId;
  delete existingData.cargoNumber;
  const party = (value) => ({
    name: text(value?.name),
    company: text(value?.company),
    email: text(value?.email),
    phone: text(value?.phone),
    addressText: text(value?.addressText),
  });
  return {
    ...existingData,
    cargoId,
    contentId: text(existing?.contentId || cargoId),
    schemaVersion: Number(existing?.schemaVersion) || 1,
    ...(preserveLegacyBoth ? {} : { contactRole }),
    cargoName: text(form.cargoName),
    quantity: Number(form.quantity),
    unit: text(form.unit) || "件",
    note: text(form.note),
    sender: contactRole === "SENDER"
      ? party(form.sender)
      : preserveLegacyBoth ? party(existing.sender) : emptyParty(),
    recipient: contactRole === "RECIPIENT"
      ? party(form.recipient)
      : preserveLegacyBoth ? party(existing.recipient) : emptyParty(),
    assignmentStatus: existing?.assignmentStatus ?? "UNASSIGNED",
    createdByUid: text(existing?.createdByUid || user?.uid),
    updatedByUid: text(user?.uid),
    createdAt: existing?.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

function plainForm(record) {
  return {
    contactRole: cargoContactRole(record) === "RECIPIENT" ? "RECIPIENT" : "SENDER",
    roleChanged: false,
    cargoName: text(record?.cargoName),
    quantity: record?.quantity === undefined ? "1" : String(record.quantity),
    unit: text(record?.unit) || "件",
    note: text(record?.note),
    sender: {
      name: text(record?.sender?.name),
      company: text(record?.sender?.company),
      email: text(record?.sender?.email),
      phone: text(record?.sender?.phone),
      addressText: text(record?.sender?.addressText),
    },
    recipient: {
      name: text(record?.recipient?.name),
      company: text(record?.recipient?.company),
      email: text(record?.recipient?.email),
      phone: text(record?.recipient?.phone),
      addressText: text(record?.recipient?.addressText),
    },
  };
}

function recordTimestamp(record) {
  const value = record?.createdAt;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value === "number") return value;
  return 0;
}

function makeChunkData(chunkId, sequence, items, isNew = false) {
  return {
    chunkId,
    sequence,
    itemCount: Object.keys(items).length,
    estimatedBytes: estimateItemsBytes(items),
    targetBytes: CARGO_CHUNK_TARGET_BYTES,
    items,
    ...(isNew ? { createdAt: serverTimestamp() } : {}),
    updatedAt: serverTimestamp(),
  };
}

function normalizeChunkIds(meta) {
  return Array.isArray(meta?.chunkIds)
    ? [...new Set(meta.chunkIds.map(text).filter(Boolean))]
    : [];
}

function activeChunkIdFromMeta(meta, chunkIds) {
  const activeChunkId = text(meta?.activeChunkId);
  return chunkIds.includes(activeChunkId) ? activeChunkId : chunkIds[chunkIds.length - 1] || "";
}

function validMetaItemCount(meta, chunkIds) {
  if (!chunkIds.length) return 0;
  const count = Number(meta?.totalItemCount);
  return Number.isSafeInteger(count) && count > 0 ? count : null;
}

async function countItemsInTransaction(transaction, orgId, chunkIds, knownSnapshots = {}) {
  let count = 0;
  for (const chunkId of chunkIds) {
    const snapshot = knownSnapshots[chunkId] || await transaction.get(cargoChunkRef(orgId, chunkId));
    if (!snapshot.exists()) continue;
    const items = snapshot.data()?.items || {};
    count += Object.keys(items).length;
  }
  return count;
}

async function currentItemCount(transaction, orgId, meta, chunkIds, knownSnapshots = {}) {
  const count = validMetaItemCount(meta, chunkIds);
  return count === null
    ? countItemsInTransaction(transaction, orgId, chunkIds, knownSnapshots)
    : count;
}

async function repairCargoManifest(orgId, expectedChunkIds) {
  const metaRef = cargoMetaRef(orgId);
  await runTransaction(db, async (transaction) => {
    const metaSnapshot = await transaction.get(metaRef);
    if (!metaSnapshot.exists()) return;
    const meta = metaSnapshot.data() || {};
    const chunkIds = normalizeChunkIds(meta);
    if (
      chunkIds.length !== expectedChunkIds.length ||
      chunkIds.some((chunkId, index) => chunkId !== expectedChunkIds[index])
    ) return;

    const existingChunkIds = [];
    let totalItemCount = 0;
    for (const chunkId of chunkIds) {
      const snapshot = await transaction.get(cargoChunkRef(orgId, chunkId));
      if (!snapshot.exists()) continue;
      existingChunkIds.push(chunkId);
      totalItemCount += Object.keys(snapshot.data()?.items || {}).length;
    }
    const rawChunkCount = Array.isArray(meta.chunkIds) ? meta.chunkIds.length : 0;
    const activeChunkId = activeChunkIdFromMeta(meta, existingChunkIds) || null;
    const needsRepair =
      rawChunkCount !== existingChunkIds.length ||
      Number(meta.chunkCount) !== existingChunkIds.length ||
      Number(meta.totalItemCount) !== totalItemCount ||
      (meta.activeChunkId || null) !== activeChunkId;
    if (!needsRepair) return;

    transaction.set(metaRef, {
      chunkIds: existingChunkIds,
      chunkCount: existingChunkIds.length,
      totalItemCount,
      activeChunkId,
      targetBytes: CARGO_CHUNK_TARGET_BYTES,
      nextChunkSequence: nextChunkSequence(meta, existingChunkIds),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
}

async function loadCargoRegistry(orgId) {
  const metaSnapshot = await getDoc(cargoMetaRef(orgId));
  if (!metaSnapshot.exists()) return { records: [], chunkCount: 0 };
  const meta = metaSnapshot.data() || {};
  const chunkIds = normalizeChunkIds(meta);
  if (!chunkIds.length) {
    if (
      (Array.isArray(meta.chunkIds) && meta.chunkIds.length > 0) ||
      Number(meta.chunkCount) !== 0 ||
      Number(meta.totalItemCount) !== 0 ||
      Boolean(meta.activeChunkId)
    ) await repairCargoManifest(orgId, chunkIds);
    return { records: [], chunkCount: 0 };
  }

  const snapshots = await Promise.all(
    chunkIds.map((chunkId) => getDoc(cargoChunkRef(orgId, chunkId))),
  );
  const records = [];
  const existingChunkIds = [];
  let totalItemCount = 0;
  snapshots.forEach((snapshot, index) => {
    if (!snapshot.exists()) return;
    const chunkId = chunkIds[index];
    const items = snapshot.data()?.items || {};
    existingChunkIds.push(chunkId);
    totalItemCount += Object.keys(items).length;
    Object.entries(items).forEach(([cargoId, record]) => {
      records.push({ ...record, cargoId: text(record?.cargoId) || cargoId, _chunkId: chunkId });
    });
  });
  const activeChunkId = activeChunkIdFromMeta(meta, existingChunkIds) || null;
  if (
    (Array.isArray(meta.chunkIds) ? meta.chunkIds.length : 0) !== existingChunkIds.length ||
    Number(meta.chunkCount) !== existingChunkIds.length ||
    Number(meta.totalItemCount) !== totalItemCount ||
    (meta.activeChunkId || null) !== activeChunkId
  ) await repairCargoManifest(orgId, chunkIds);
  records.sort((a, b) => recordTimestamp(b) - recordTimestamp(a) || a.cargoId.localeCompare(b.cargoId));
  return { records, chunkCount: existingChunkIds.length };
}

function validateForm(form) {
  if (!text(form.cargoName)) return "請輸入貨物名稱。";
  const quantity = Number(form.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return "數量必須大於 0。";
  if (!text(form.unit)) return "請輸入數量單位。";
  const isRecipient = text(form.contactRole).toUpperCase() === "RECIPIENT";
  const role = isRecipient ? "recipient" : "sender";
  const roleLabel = isRecipient ? "收件人" : "寄件人";
  const addressLabel = isRecipient ? "送貨地址" : "取貨地址";
  if (!text(form[role]?.name)) return `請填寫${roleLabel}姓名。`;
  if (!text(form[role]?.addressText)) return `請填寫${addressLabel}。`;
  return "";
}

function PartySummary({ role, party }) {
  const isRecipient = role.includes("收件人");
  return (
    <div className={`dmc-party ${isRecipient ? "dmc-party-recipient" : ""}`}>
      <div className="dmc-party-label">
        <span className="dmc-party-dot" />
        {role}
      </div>
      <div className="dmc-party-name">{text(party?.name) || "未填姓名"}</div>
      <div className="dmc-party-meta">
        {[text(party?.company), text(party?.phone), text(party?.email)].filter(Boolean).join("・") || "尚無公司／電話／Email資料"}
      </div>
      <div className="dmc-party-address">{text(party?.addressText) || "尚未填寫地址"}</div>
    </div>
  );
}

function CargoPartiesSummary({ record }) {
  const role = cargoContactRole(record);
  if (role === "SENDER") {
    return <div className="dmc-party-grid dmc-party-grid-single"><PartySummary role="寄件人／取貨" party={record.sender} /></div>;
  }
  if (role === "RECIPIENT") {
    return <div className="dmc-party-grid dmc-party-grid-single"><PartySummary role="收件人／送貨" party={record.recipient} /></div>;
  }
  return (
    <div className="dmc-party-grid">
      <PartySummary role="寄件人／取貨" party={record.sender} />
      <PartySummary role="收件人／送貨" party={record.recipient} />
    </div>
  );
}

function addressGroupKey(value) {
  return text(value).normalize("NFKC").replace(/\s+/g, "").toLocaleLowerCase();
}

function contactGroupKey(party) {
  return [party?.name, party?.company, party?.phone, party?.email]
    .map((value) => text(value).normalize("NFKC").toLocaleLowerCase())
    .join("|");
}

function makeWorkRecipientId(usedIds) {
  let id = "";
  do {
    id = Array.from({ length: 10 }, () => CARGO_CODE_CHARS[
      Math.floor(Math.random() * CARGO_CODE_CHARS.length)
    ]).join("");
  } while (usedIds.has(id));
  usedIds.add(id);
  return id;
}

function groupCargoRecordsForWork(records, mode) {
  const groupsByKey = new Map();
  const usedRecipientIds = new Set();
  const operations = mode === "single"
    ? [
        { operationType: "PICKUP", partyKey: "sender" },
        { operationType: "DELIVERY", partyKey: "recipient" },
      ]
    : [{ operationType: "DELIVERY", partyKey: "recipient" }];

  records.forEach((record) => {
    const recordRole = cargoContactRole(record);
    operations.forEach(({ operationType, partyKey }) => {
      const requiredRole = partyKey === "sender" ? "SENDER" : "RECIPIENT";
      const hasSelectedParty = recordRole === "BOTH" || recordRole === requiredRole;
      const party = hasSelectedParty ? record?.[partyKey] || {} : emptyParty();
      const addressText = text(party.addressText);
      const normalizedAddress = addressGroupKey(addressText);
      const groupKey = `${operationType}|${normalizedAddress}`;
      if (!groupsByKey.has(groupKey)) {
        groupsByKey.set(groupKey, {
          id: `cargo-${operationType.toLowerCase()}-${groupsByKey.size + 1}`,
          sequence: groupsByKey.size,
          operationType,
          addressText,
          normalizedAddress,
          requiresCompletion: !hasSelectedParty,
          contacts: [],
        });
      }
      const group = groupsByKey.get(groupKey);
      if (!hasSelectedParty) group.requiresCompletion = true;
      const identityKey = hasSelectedParty
        ? contactGroupKey(party)
        : `missing:${text(record.cargoId)}:${operationType}`;
      let contact = group.contacts.find((item) => item.identityKey === identityKey);
      if (!contact) {
        contact = {
          id: makeWorkRecipientId(usedRecipientIds),
          identityKey,
          name: text(party.name),
          company: text(party.company),
          email: text(party.email),
          phone: text(party.phone),
          addressText,
          cargos: [],
        };
        group.contacts.push(contact);
      }
      if (!contact.cargos.some((item) => text(item.cargoId) === text(record.cargoId))) {
        contact.cargos.push(record);
      }
    });
  });

  return [...groupsByKey.values()].sort((left, right) => {
    if (left.operationType !== right.operationType) {
      return left.operationType === "PICKUP" ? -1 : 1;
    }
    return left.sequence - right.sequence;
  });
}

function buildCargoPlanStops(mode, groups) {
  return groups.map((group, index) => ({
    id: mode === "single"
      ? group.operationType === "PICKUP" ? "SRT" : "END"
      : `CG${String(index + 1).padStart(3, "0")}`,
    order: index + 1,
    timeWindowType: "NONE",
    earliestStart: "",
    latestStart: "",
    serviceMinutes: 0,
    role: mode === "single"
      ? group.operationType === "PICKUP" ? "start" : "end"
      : "waypoint",
    operationType: group.operationType,
    text: group.addressText,
    lat: null,
    lng: null,
    verified: false,
    city: "",
    suburb: "",
    road: "",
    houseNumber: "",
    postalCode: "",
    distanceFromPreviousKm: index === 0 ? 0 : null,
    durationFromPreviousMinutes: index === 0 ? 0 : null,
    recipients: group.contacts.map((contact) => ({
      id: contact.id,
      recipientId: contact.id,
      cargoNumber: "",
      operationType: group.operationType,
      name: contact.name,
      phone: contact.phone,
      company: contact.company,
      email: text(contact.email),
      addressText: group.addressText,
      note: "",
      sourceCargoRef: { source: "cargoInventory" },
      contents: contact.cargos.map((record) => ({
        id: text(record.contentId || record.cargoId),
        contentId: text(record.contentId || record.cargoId),
        sourceCargoId: text(record.cargoId),
        operationType: group.operationType,
        name: text(record.cargoName),
        quantity: record.quantity,
        unit: text(record.unit) || "件",
        note: text(record.note),
        sender: {
          name: text(record.sender?.name),
          company: text(record.sender?.company),
          email: text(record.sender?.email),
          phone: text(record.sender?.phone),
          addressText: text(record.sender?.addressText),
        },
      })),
    })),
    photos: [],
  }));
}

export default function DriverManagerCargoManagement({ user, profile, profileLoading = false, onCreateWork }) {
  const orgId = text(profile?.orgId);
  const [records, setRecords] = useState([]);
  const [chunkCount, setChunkCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [selectedCargoIds, setSelectedCargoIds] = useState([]);
  const [workReview, setWorkReview] = useState(null);
  const [workPlanError, setWorkPlanError] = useState("");
  const [commonContacts, setCommonContacts] = useState([]);
  const [commonSearch, setCommonSearch] = useState("");
  const [commonLoading, setCommonLoading] = useState(false);
  const [commonError, setCommonError] = useState("");
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const commonContactPageRef = useRef(0);
  const commonContactHasMoreRef = useRef(true);
  const commonContactLoadingRef = useRef(false);
  const commonContactOrgRef = useRef(orgId);
  const commonContactRequestRef = useRef(0);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setLoadError("");
      if (!orgId) {
        setRecords([]);
        setChunkCount(0);
        if (profileLoading) return;
        setLoadError("目前帳號尚未取得組織資料，無法載入貨物管理。請重新整理或確認組織權限。");
        setLoading(false);
        return;
      }
      try {
        const result = await loadCargoRegistry(orgId);
        if (!alive) return;
        setRecords(result.records);
        setChunkCount(result.chunkCount);
      } catch (error) {
        console.error("[DriverManagerCargoManagement] load registry:", error);
        if (!alive) return;
        setLoadError(error?.message || "貨物清單載入失敗，請稍後重試。");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [orgId, profileLoading, reloadKey]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    commonContactOrgRef.current = orgId;
    commonContactRequestRef.current += 1;
    setCommonContacts([]);
    setCommonSearch("");
    setCommonError("");
    setContactPickerOpen(false);
    commonContactPageRef.current = 0;
    commonContactHasMoreRef.current = true;
    commonContactLoadingRef.current = false;
  }, [orgId]);

  useEffect(() => {
    setSelectedCargoIds((current) => current.filter((cargoId) =>
      records.some((record) => record.cargoId === cargoId),
    ));
  }, [records]);

  const filteredRecords = useMemo(() => {
    const keyword = text(search).toLocaleLowerCase();
    const matches = keyword ? records.filter((record) => {
      const haystack = [
        record.contentId,
        record.cargoId,
        record.cargoNumber,
        record.cargoName,
        record.sender?.name,
        record.sender?.company,
        record.sender?.email,
        record.sender?.phone,
        record.sender?.addressText,
        record.recipient?.name,
        record.recipient?.company,
        record.recipient?.email,
        record.recipient?.phone,
        record.recipient?.addressText,
      ].map(text).join(" ").toLocaleLowerCase();
      return haystack.includes(keyword);
    }) : records;
    return [
      ...matches.filter((record) => record.assignmentStatus === "UNASSIGNED"),
      ...matches.filter((record) => record.assignmentStatus !== "UNASSIGNED"),
    ];
  }, [records, search]);

  const filteredCommonContacts = useMemo(() => {
    const keyword = text(commonSearch).toLocaleLowerCase();
    if (!keyword) return commonContacts;
    return commonContacts.filter((item) => [
      item.name,
      item.displayName,
      item.company,
      item.companyName,
      item.organization,
      item.phone,
      item.email,
      item.addressText,
      item.address,
      item.fullAddress,
      item.formattedAddress,
    ].map(text).join(" ").toLocaleLowerCase().includes(keyword));
  }, [commonContacts, commonSearch]);

  const selectedCargoRecords = useMemo(() => records.filter((record) =>
    selectedCargoIds.includes(record.cargoId),
  ), [records, selectedCargoIds]);
  const filteredSelectableCargoIds = useMemo(() => filteredRecords
    .map((record) => text(record.cargoId))
    .filter(Boolean), [filteredRecords]);
  const assignedCargoRecords = useMemo(
    () => records.filter((record) => record.assignmentStatus !== "UNASSIGNED"),
    [records],
  );
  const allFilteredCargoSelected = filteredSelectableCargoIds.length > 0 &&
    filteredSelectableCargoIds.every((cargoId) => selectedCargoIds.includes(cargoId));

  const reviewSingleValid = workReview?.mode !== "single" || (
    workReview.groups.filter((group) => group.operationType === "PICKUP").length === 1 &&
    workReview.groups.filter((group) => group.operationType === "DELIVERY").length === 1
  );
  const reviewMultiValid = workReview?.mode !== "multi" || workReview.groups.length >= 2;
  const reviewAddressValid = Boolean(workReview && workReview.groups.every((group) => addressGroupKey(group.addressText) || group.requiresCompletion));
  const reviewCargoCoverageValid = Boolean(workReview && (() => {
    const selectedIds = new Set(selectedCargoRecords.map((record) => text(record.cargoId)));
    const idsForOperation = (operationType) => new Set(workReview.groups
      .filter((group) => group.operationType === operationType)
      .flatMap((group) => group.contacts.flatMap((contact) => contact.cargos.map((record) => text(record.cargoId)))));
    if (workReview.mode === "single") {
      const pickupIds = idsForOperation("PICKUP");
      const deliveryIds = idsForOperation("DELIVERY");
      return [...selectedIds].every((cargoId) => pickupIds.has(cargoId) && deliveryIds.has(cargoId));
    }
    const deliveryIds = idsForOperation("DELIVERY");
    return [...selectedIds].every((cargoId) => deliveryIds.has(cargoId));
  })());
  const reviewCanCreate = Boolean(workReview && reviewSingleValid && reviewMultiValid && reviewAddressValid && reviewCargoCoverageValid && selectedCargoRecords.length && onCreateWork);

  const toggleCargoSelection = (record) => {
    const cargoId = text(record?.cargoId);
    if (!cargoId) return;
    setSelectedCargoIds((current) => current.includes(cargoId)
      ? current.filter((value) => value !== cargoId)
      : [...current, cargoId]);
  };

  const toggleFilteredCargoSelection = () => {
    if (!filteredSelectableCargoIds.length) return;
    const visibleIds = new Set(filteredSelectableCargoIds);
    setSelectedCargoIds((current) => {
      const allSelected = filteredSelectableCargoIds.every((cargoId) => current.includes(cargoId));
      return allSelected
        ? current.filter((cargoId) => !visibleIds.has(cargoId))
        : [...new Set([...current, ...visibleIds])];
    });
  };

  const beginWorkGrouping = (mode) => {
    const selected = records.filter((record) => selectedCargoIds.includes(record.cargoId));
    if (!selected.length) {
      setNotice("請先勾選要建立工作的貨物。");
      return;
    }

    const usedContentIds = new Set(records.flatMap((record) => [
      text(record.contentId),
      text(record.cargoId),
    ]).filter(Boolean));
    const workRecords = selected.map((record) => {
      const cargoId = text(record.cargoId);
      const currentContentId = text(record.contentId) || cargoId;
      const assignmentStatus = text(record.assignmentStatus).toUpperCase() || "UNASSIGNED";
      const needsFreshContentId = assignmentStatus !== "UNASSIGNED" && currentContentId === cargoId;
      let workContentId = currentContentId;
      if (needsFreshContentId) {
        do {
          workContentId = makeCargoCode();
        } while (usedContentIds.has(workContentId));
        usedContentIds.add(workContentId);
      }
      return { ...record, contentId: workContentId };
    });
    const cargoRefs = selected.map((record, index) => ({
      cargoId: text(record.cargoId),
      chunkId: text(record._chunkId),
      expectedContentId: text(record.contentId) || text(record.cargoId),
      workContentId: text(workRecords[index]?.contentId),
    }));

    setWorkReview({ mode, groups: groupCargoRecordsForWork(workRecords, mode), cargoRefs });
    setWorkPlanError("");
  };

  const beginCreate = () => {
    setEditingRecord(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setModalOpen(true);
  };

  const beginEdit = (record) => {
    setEditingRecord(record);
    setForm(plainForm(record));
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setContactPickerOpen(false);
    setEditingRecord(null);
    setFormError("");
  };

  const changeField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const changeParty = (partyKey, field, value) => {
    setForm((current) => ({
      ...current,
      [partyKey]: { ...current[partyKey], [field]: value },
    }));
  };

  const refresh = useCallback(() => setReloadKey((value) => value + 1), []);

  const loadCommonContactPage = async (requestedPage = null) => {
    if (!orgId || commonContactLoadingRef.current || !commonContactHasMoreRef.current) return;
    const pageNo = requestedPage || commonContactPageRef.current + 1;
    if (pageNo <= commonContactPageRef.current) return;
    const requestedOrgId = orgId;
    const requestId = commonContactRequestRef.current;
    commonContactLoadingRef.current = true;
    setCommonLoading(true);
    setCommonError("");
    try {
      const config = directoryConfig("contacts");
      const snapshot = await getDoc(
        doc(db, "orgs", requestedOrgId, config.collection, directoryPageId(pageNo)),
      );
      if (
        requestId !== commonContactRequestRef.current ||
        commonContactOrgRef.current !== requestedOrgId
      ) return;
      const page = hydrateDirectoryPage(snapshot, pageNo);
      if (!page.exists) {
        commonContactHasMoreRef.current = false;
        return;
      }
      setCommonContacts((current) => sortDirectoryItems([...current, ...page.items]));
      commonContactPageRef.current = pageNo;
      commonContactHasMoreRef.current = page.items.length >= config.pageCapacity;
    } catch (error) {
      if (requestId !== commonContactRequestRef.current) return;
      console.error("[DriverManagerCargoManagement] common contacts:", error);
      setCommonError("常用聯絡人讀取失敗，請確認組織權限後重試。");
    } finally {
      if (requestId === commonContactRequestRef.current) {
        commonContactLoadingRef.current = false;
        setCommonLoading(false);
      }
    }
  };

  const openCommonContactPicker = async () => {
    if (!orgId) {
      if (profileLoading) return;
      setFormError("目前帳號尚未取得組織資料，無法讀取常用聯絡人。");
      return;
    }
    setContactPickerOpen(true);
    setCommonSearch("");
    setCommonError("");
    if (commonContactPageRef.current === 0) await loadCommonContactPage(1);
  };

  const applyCommonContact = (item) => {
    const partyKey = text(form.contactRole).toUpperCase() === "RECIPIENT"
      ? "recipient"
      : "sender";
    setForm((current) => ({
      ...current,
      [partyKey]: {
        name: text(item?.name || item?.displayName),
        company: text(item?.company || item?.companyName || item?.organization),
        email: text(item?.email),
        phone: text(item?.phone || item?.tel || item?.mobile),
        addressText: text(item?.addressText || item?.address || item?.fullAddress || item?.formattedAddress),
      },
    }));
    setFormError("");
    setContactPickerOpen(false);
  };

  const changeContactRole = (contactRole) => {
    setForm((current) => {
      const currentRole = text(current.contactRole).toUpperCase() === "RECIPIENT"
        ? "RECIPIENT"
        : "SENDER";
      if (contactRole === currentRole) return current;
      const currentKey = currentRole === "RECIPIENT" ? "recipient" : "sender";
      const nextKey = contactRole === "RECIPIENT" ? "recipient" : "sender";
      const currentParty = current[currentKey] || emptyParty();
      const nextParty = current[nextKey] || emptyParty();
      const partyToMove = editingRecord && hasPartyData(nextParty)
        ? nextParty
        : currentParty;
      return {
        ...current,
        contactRole,
        roleChanged: true,
        [currentKey]: emptyParty(),
        [nextKey]: { ...partyToMove },
      };
    });
    setFormError("");
  };

  const updateReviewGroupAddress = (groupId, addressText) => {
    setWorkReview((current) => current ? {
      ...current,
      groups: current.groups.map((group) => group.id === groupId
        ? { ...group, addressText }
        : group),
    } : current);
    setWorkPlanError("");
  };

  const createWorkFromReview = () => {
    if (!workReview || !reviewCanCreate || !onCreateWork) {
      const message = !reviewAddressValid
        ? "已有聯絡人資料的站點請填寫地址；缺少端點資料可留白並在建單表單補齊。"
        : !reviewCargoCoverageValid
          ? "分組中有貨物未包含在必要站點，請重新整理分組。"
          : workReview?.mode === "single"
            ? "單點工作必須只有一個取貨地址與一個送貨地址；請拆併站點或改用多點工作。"
            : "多點工作至少需要兩個不同的送貨地址。請檢查分組，或改用單點工作。";
      setWorkPlanError(message);
      return;
    }
    const groups = workReview.mode === "single"
      ? [...workReview.groups].sort((left, right) => left.operationType === "PICKUP" ? -1 : right.operationType === "PICKUP" ? 1 : 0)
      : workReview.groups;
    const plan = {
      mode: workReview.mode,
      stops: buildCargoPlanStops(workReview.mode, groups),
      cargoRefs: Array.isArray(workReview.cargoRefs)
        ? workReview.cargoRefs
        : selectedCargoRecords.map((record) => ({
            cargoId: text(record.cargoId),
            chunkId: text(record._chunkId),
            expectedContentId: text(record.contentId) || text(record.cargoId),
            workContentId: text(record.contentId) || text(record.cargoId),
          })),
    };
    onCreateWork(plan);
    setWorkReview(null);
    setSelectedCargoIds([]);
    setWorkPlanError("");
  };

  const saveCargo = async (event) => {
    event.preventDefault();
    const validationError = validateForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (!orgId) {
      setFormError("目前帳號尚未取得組織資料，無法保存。");
      return;
    }

    const record = buildCargoRecord(form, editingRecord, user, records.map((item) => item.cargoId));
    const loneItemBytes = estimateItemsBytes({ [record.cargoId]: record });
    if (loneItemBytes > CARGO_CHUNK_TARGET_BYTES) {
      setFormError("這筆貨物資料超過單筆安全容量，請縮短地址或備註內容。");
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const metaRef = cargoMetaRef(orgId);
      await runTransaction(db, async (transaction) => {
        const metaSnapshot = await transaction.get(metaRef);
        const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
        let chunkIds = normalizeChunkIds(meta);
        let nextSequence = nextChunkSequence(meta, chunkIds);

        if (editingRecord) {
          const oldChunkId = text(editingRecord._chunkId);
          if (!oldChunkId) throw new Error("找不到貨物所在分片，請重新整理後再編輯。");
          if (!chunkIds.includes(oldChunkId)) throw new Error("貨物分片清單已更新，請重新整理後再編輯。");
          const oldChunkRef = cargoChunkRef(orgId, oldChunkId);
          const oldChunkSnapshot = await transaction.get(oldChunkRef);
          if (!oldChunkSnapshot.exists()) throw new Error("貨物分片已更新，請重新整理清單後再試。");
          const oldChunk = oldChunkSnapshot.data() || {};
          const oldItems = oldChunk.items && typeof oldChunk.items === "object" ? oldChunk.items : {};
          if (!Object.prototype.hasOwnProperty.call(oldItems, record.cargoId)) {
            throw new Error("找不到這筆貨物，請重新整理清單後再試。");
          }
          const nextItems = { ...oldItems, [record.cargoId]: record };
          const nextBytes = estimateItemsBytes(nextItems);
          const itemCount = await currentItemCount(transaction, orgId, meta, chunkIds, {
            [oldChunkId]: oldChunkSnapshot,
          });

          if (nextBytes <= CARGO_CHUNK_TARGET_BYTES) {
            transaction.set(oldChunkRef, {
              ...makeChunkData(oldChunkId, Number(oldChunk.sequence) || sequenceFromChunkId(oldChunkId), nextItems),
              createdAt: oldChunk.createdAt || serverTimestamp(),
            }, { merge: true });
            transaction.set(metaRef, {
              schemaVersion: 1,
              chunkIds,
              chunkCount: chunkIds.length,
              totalItemCount: itemCount,
              activeChunkId: activeChunkIdFromMeta(meta, chunkIds) || null,
              targetBytes: CARGO_CHUNK_TARGET_BYTES,
              nextChunkSequence: nextSequence,
              updatedAt: serverTimestamp(),
            }, { merge: true });
            return { recordChunkId: oldChunkId };
          }

          const remainingItems = { ...oldItems };
          delete remainingItems[record.cargoId];
          const reservedChunk = await reserveCargoChunkId(transaction, orgId, nextSequence);
          const newChunkId = reservedChunk.chunkId;
          nextSequence = reservedChunk.nextSequence;
          const newChunkRef = cargoChunkRef(orgId, newChunkId);
          if (Object.keys(remainingItems).length) {
            transaction.set(oldChunkRef, {
              ...makeChunkData(oldChunkId, Number(oldChunk.sequence) || sequenceFromChunkId(oldChunkId), remainingItems),
              createdAt: oldChunk.createdAt || serverTimestamp(),
            }, { merge: true });
          } else {
            transaction.delete(oldChunkRef);
            chunkIds = chunkIds.filter((id) => id !== oldChunkId);
          }
          transaction.set(newChunkRef, makeChunkData(newChunkId, nextSequence - 1, { [record.cargoId]: record }, true));
          chunkIds = [...chunkIds.filter((id) => id !== newChunkId), newChunkId];
          transaction.set(metaRef, {
            schemaVersion: 1,
            chunkIds,
            chunkCount: chunkIds.length,
            totalItemCount: itemCount,
            activeChunkId: newChunkId,
            targetBytes: CARGO_CHUNK_TARGET_BYTES,
            nextChunkSequence: nextSequence,
            updatedAt: serverTimestamp(),
          }, { merge: true });
          return { recordChunkId: newChunkId };
        }

        let activeChunkId = activeChunkIdFromMeta(meta, chunkIds);
        let activeChunk = null;
        let activeChunkRef = null;
        const knownSnapshots = {};
        const knownCargoIds = new Set(records.map((item) => text(item.cargoId)));
        let candidateCargoId = record.cargoId;
        let codeSnapshot = await transaction.get(cargoCodeRef(orgId, candidateCargoId));
        let collisionAttempts = 0;
        while (knownCargoIds.has(candidateCargoId) || codeSnapshot.exists()) {
          if (collisionAttempts >= 10) throw new Error("無法產生唯一貨物編號，請重新按一次儲存。");
          candidateCargoId = makeCargoCode();
          codeSnapshot = await transaction.get(cargoCodeRef(orgId, candidateCargoId));
          collisionAttempts += 1;
        }
        record.cargoId = candidateCargoId;
        record.contentId = candidateCargoId;
        if (activeChunkId && chunkIds.includes(activeChunkId)) {
          activeChunkRef = cargoChunkRef(orgId, activeChunkId);
          const activeSnapshot = await transaction.get(activeChunkRef);
          knownSnapshots[activeChunkId] = activeSnapshot;
          if (activeSnapshot.exists()) {
            activeChunk = activeSnapshot.data() || {};
          } else {
            chunkIds = chunkIds.filter((id) => id !== activeChunkId);
            activeChunkId = "";
            activeChunkRef = null;
          }
        }
        const activeItems = activeChunk?.items && typeof activeChunk.items === "object" ? activeChunk.items : {};
        if (Object.prototype.hasOwnProperty.call(activeItems, record.cargoId)) {
          throw new Error("貨物編號重複，請重新按一次儲存。");
        }
        const candidateItems = { ...activeItems, [record.cargoId]: record };
        const candidateBytes = estimateItemsBytes(candidateItems);
        const currentCount = await currentItemCount(transaction, orgId, meta, chunkIds, knownSnapshots);

        if (activeChunk && candidateBytes <= CARGO_CHUNK_TARGET_BYTES) {
          transaction.set(activeChunkRef, {
            ...makeChunkData(activeChunkId, Number(activeChunk.sequence) || sequenceFromChunkId(activeChunkId), candidateItems),
            createdAt: activeChunk.createdAt || serverTimestamp(),
          }, { merge: true });
          transaction.set(cargoCodeRef(orgId, record.cargoId), {
            cargoId: record.cargoId,
            createdAt: serverTimestamp(),
          });
          transaction.set(metaRef, {
            schemaVersion: 1,
            chunkIds,
            chunkCount: chunkIds.length,
            totalItemCount: currentCount + 1,
            activeChunkId,
            targetBytes: CARGO_CHUNK_TARGET_BYTES,
            nextChunkSequence: nextSequence,
            updatedAt: serverTimestamp(),
            ...(meta.createdAt ? {} : { createdAt: serverTimestamp() }),
          }, { merge: true });
          return { recordChunkId: activeChunkId };
        }

        const reservedChunk = await reserveCargoChunkId(transaction, orgId, nextSequence);
        const newChunkId = reservedChunk.chunkId;
        nextSequence = reservedChunk.nextSequence;
        transaction.set(cargoChunkRef(orgId, newChunkId), makeChunkData(newChunkId, nextSequence - 1, { [record.cargoId]: record }, true));
        transaction.set(cargoCodeRef(orgId, record.cargoId), {
          cargoId: record.cargoId,
          createdAt: serverTimestamp(),
        });
        chunkIds = [...chunkIds.filter((id) => id !== newChunkId), newChunkId];
        transaction.set(metaRef, {
          schemaVersion: 1,
          chunkIds,
          chunkCount: chunkIds.length,
          totalItemCount: currentCount + 1,
          activeChunkId: newChunkId,
          targetBytes: CARGO_CHUNK_TARGET_BYTES,
          nextChunkSequence: nextSequence,
          updatedAt: serverTimestamp(),
          ...(meta.createdAt ? {} : { createdAt: serverTimestamp() }),
        }, { merge: true });
        return { recordChunkId: newChunkId };
      });

      setModalOpen(false);
      setEditingRecord(null);
      setNotice(editingRecord ? "貨物資料已更新。" : `貨物已建立，編號 ${record.cargoId}。`);
      refresh();
    } catch (error) {
      console.error("[DriverManagerCargoManagement] save cargo:", error);
      setFormError(error?.message || "保存失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  };

  const deleteCargo = async (record) => {
    const cargoId = text(record?.cargoId);
    const chunkId = text(record?._chunkId);
    if (!orgId || !cargoId || !chunkId || deletingId || bulkDeleting) return;
    if (!window.confirm(`確定刪除貨物「${text(record.cargoName) || cargoId}」？此操作無法復原。`)) return;

    setDeletingId(cargoId);
    setLoadError("");
    try {
      const metaRef = cargoMetaRef(orgId);
      await runTransaction(db, async (transaction) => {
        const metaSnapshot = await transaction.get(metaRef);
        const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
        const currentChunkIds = normalizeChunkIds(meta);
        if (!currentChunkIds.includes(chunkId)) throw new Error("貨物分片清單已更新，請重新整理後再刪除。");
        const chunkRef = cargoChunkRef(orgId, chunkId);
        const chunkSnapshot = await transaction.get(chunkRef);
        if (!chunkSnapshot.exists()) throw new Error("貨物分片已更新，請重新整理後再試。");
        const chunk = chunkSnapshot.data() || {};
        const items = chunk.items && typeof chunk.items === "object" ? { ...chunk.items } : {};
        if (!Object.prototype.hasOwnProperty.call(items, cargoId)) throw new Error("找不到這筆貨物，請重新整理清單後再試。");
        const totalItemCount = await currentItemCount(transaction, orgId, meta, currentChunkIds, {
          [chunkId]: chunkSnapshot,
        });
        delete items[cargoId];
        let chunkIds = currentChunkIds.filter((id) => id !== chunkId || Object.keys(items).length > 0);
        if (Object.keys(items).length) {
          transaction.set(chunkRef, {
            ...makeChunkData(chunkId, Number(chunk.sequence) || sequenceFromChunkId(chunkId), items),
            createdAt: chunk.createdAt || serverTimestamp(),
          }, { merge: true });
        } else {
          transaction.delete(chunkRef);
        }
        transaction.delete(cargoCodeRef(orgId, cargoId));
        transaction.set(metaRef, {
          schemaVersion: 1,
          chunkIds,
          chunkCount: chunkIds.length,
          totalItemCount: Math.max(0, totalItemCount - 1),
          activeChunkId: chunkIds[chunkIds.length - 1] || null,
          targetBytes: CARGO_CHUNK_TARGET_BYTES,
          nextChunkSequence: nextChunkSequence(meta, chunkIds),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      });
      setNotice(`貨物 ${cargoId} 已刪除。`);
      refresh();
    } catch (error) {
      console.error("[DriverManagerCargoManagement] delete cargo:", error);
      setLoadError(error?.message || "刪除失敗，請稍後再試。");
    } finally {
      setDeletingId("");
    }
  };

  const deleteAssignedCargo = async () => {
    const requestedCount = assignedCargoRecords.length;
    if (!orgId || !requestedCount || bulkDeleting || deletingId || saving) return;
    const confirmed = window.confirm(
      `確定刪除貨物管理中的 ${requestedCount} 筆已派單貨物？\n` +
      "只會刪除貨物主檔，不會刪除已建立的工作資料。此操作無法復原。",
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    setLoadError("");
    let deletedCount = 0;
    try {
      const targetChunkIds = [...new Set(
        assignedCargoRecords.map((record) => text(record._chunkId)).filter(Boolean),
      )];
      if (!targetChunkIds.length) {
        throw new Error("找不到已派單貨物所在的分片，請重新整理後再試。");
      }
      const metaRef = cargoMetaRef(orgId);

      for (const chunkId of targetChunkIds) {
        while (true) {
          const removedCount = await runTransaction(db, async (transaction) => {
            const metaSnapshot = await transaction.get(metaRef);
            const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
            const chunkIds = normalizeChunkIds(meta);
            if (!chunkIds.includes(chunkId)) return 0;

            const chunkRef = cargoChunkRef(orgId, chunkId);
            const chunkSnapshot = await transaction.get(chunkRef);
            if (!chunkSnapshot.exists()) return 0;
            const chunk = chunkSnapshot.data() || {};
            const items = chunk.items && typeof chunk.items === "object"
              ? chunk.items
              : {};
            const cargoIds = Object.entries(items)
              .filter(([, item]) => item?.assignmentStatus !== "UNASSIGNED")
              .map(([cargoId]) => cargoId)
              .slice(0, 350);
            if (!cargoIds.length) return 0;

            const totalItemCount = await currentItemCount(
              transaction,
              orgId,
              meta,
              chunkIds,
              { [chunkId]: chunkSnapshot },
            );
            const nextItems = { ...items };
            cargoIds.forEach((cargoId) => delete nextItems[cargoId]);
            const nextChunkIds = chunkIds.filter(
              (id) => id !== chunkId || Object.keys(nextItems).length > 0,
            );

            if (Object.keys(nextItems).length) {
              transaction.set(chunkRef, {
                ...makeChunkData(
                  chunkId,
                  Number(chunk.sequence) || sequenceFromChunkId(chunkId),
                  nextItems,
                ),
                createdAt: chunk.createdAt || serverTimestamp(),
              }, { merge: true });
            } else {
              transaction.delete(chunkRef);
            }
            cargoIds.forEach((cargoId) =>
              transaction.delete(cargoCodeRef(orgId, cargoId)),
            );
            transaction.set(metaRef, {
              schemaVersion: 1,
              chunkIds: nextChunkIds,
              chunkCount: nextChunkIds.length,
              totalItemCount: Math.max(0, totalItemCount - cargoIds.length),
              activeChunkId: activeChunkIdFromMeta(meta, nextChunkIds) || null,
              targetBytes: CARGO_CHUNK_TARGET_BYTES,
              nextChunkSequence: nextChunkSequence(meta, nextChunkIds),
              updatedAt: serverTimestamp(),
            }, { merge: true });
            return cargoIds.length;
          });
          if (!removedCount) break;
          deletedCount += removedCount;
        }
      }

      setNotice(
        deletedCount
          ? `已刪除 ${deletedCount} 筆已派單貨物主檔；既有工作資料未變更。`
          : "目前沒有已派單貨物需要刪除。",
      );
    } catch (error) {
      console.error("[DriverManagerCargoManagement] bulk delete assigned cargo:", error);
      setNotice(
        deletedCount
          ? `已刪除 ${deletedCount} 筆已派單貨物；後續刪除失敗：${error?.message || "請重新整理後再試。"}`
          : error?.message || "批次刪除失敗，請稍後重試。",
      );
    } finally {
      setBulkDeleting(false);
      refresh();
    }
  };

  return (
    <div className="dmc-page">
      <style>{PAGE_STYLES}</style>
      <div className="dmc-shell">
        <header className="dmc-heading">
          <div>
            <h1>貨物管理</h1>
          </div>
          <div className="dmc-heading-actions">
            <button type="button" className="dmc-button dmc-button-quiet" onClick={refresh} disabled={loading || saving}>重新整理</button>
            <button type="button" className="dmc-button dmc-button-primary" onClick={beginCreate} disabled={!orgId || loading}>＋ 新增貨物</button>
          </div>
        </header>

        {notice ? <div className="dmc-notice" role="status">{notice}</div> : null}
        {loadError ? <div className="dmc-error-banner" role="alert">{loadError}<button type="button" onClick={refresh}>重新載入</button></div> : null}


        <div className="dmc-toolbar">
          <div className="dmc-search-wrap"><span className="dmc-search-icon" aria-hidden="true">⌕</span><input className="dmc-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜尋貨物編號、名稱、聯絡人、Email 或地址" aria-label="搜尋貨物" /></div>
          <div className="dmc-result-count">
            <span>{search ? `找到 ${filteredRecords.length} 筆` : `共 ${records.length} 筆貨物`}</span>
            <div className="dmc-toolbar-actions">
              <button
                type="button"
                className="dmc-delete-assigned"
                onClick={deleteAssignedCargo}
                disabled={!assignedCargoRecords.length || loading || saving || bulkDeleting || Boolean(deletingId)}
              >
                {bulkDeleting ? "刪除中…" : `刪除已派單貨物（${assignedCargoRecords.length}）`}
              </button>
              <button
                type="button"
                className="dmc-select-all"
                onClick={toggleFilteredCargoSelection}
                disabled={!filteredSelectableCargoIds.length || bulkDeleting}
                aria-label={allFilteredCargoSelected ? "取消選取目前顯示的貨物" : "選取目前顯示的貨物"}
              >
                {allFilteredCargoSelected ? "取消全選" : "全選"}
              </button>
            </div>
          </div>
        </div>

        {selectedCargoRecords.length ? (
          <div className="dmc-selection-bar">
            <div><strong>已選取 {selectedCargoRecords.length} 筆貨物</strong></div>
            <div className="dmc-selection-actions">
              <button type="button" className="dmc-button dmc-button-primary" onClick={() => beginWorkGrouping("single")}>建立單點工作</button>
              <button type="button" className="dmc-button" onClick={() => beginWorkGrouping("multi")}>建立多點工作</button>
              <button type="button" className="dmc-action" onClick={() => setSelectedCargoIds([])}>清除選取</button>
            </div>
          </div>
        ) : null}

        <section className="dmc-content" aria-label="貨物清單">
          {loading ? <div className="dmc-loading">正在載入貨物資料…</div> : null}
          {!loading && !loadError && filteredRecords.length === 0 ? (
            <div className="dmc-empty"><div className="dmc-empty-icon" aria-hidden="true">□</div><strong>{records.length ? "沒有符合的貨物" : "目前還沒有貨物"}</strong><span>{records.length ? "試試貨物編號、貨物名稱、聯絡人或地址。" : "先建立一筆貨物主檔，之後再整理並帶入工作。"}</span>{!records.length ? <div style={{ marginTop: 18 }}><button type="button" className="dmc-button dmc-button-primary" onClick={beginCreate}>＋ 新增第一筆貨物</button></div> : null}</div>
          ) : null}
          {!loading && filteredRecords.length > 0 ? (
            <div className="dmc-list">
              {filteredRecords.map((record) => (
                <article className={`dmc-record${record.assignmentStatus !== "UNASSIGNED" ? " is-assigned" : ""}`} key={record.cargoId}>
                  <div className="dmc-record-head">
                    <div className="dmc-record-title"><input type="checkbox" className="dmc-select-checkbox" checked={selectedCargoIds.includes(record.cargoId)} onChange={() => toggleCargoSelection(record)} disabled={bulkDeleting} aria-label={`選取貨物 ${text(record.cargoName) || record.cargoId}`} /><div className="dmc-name-wrap"><div className="dmc-cargo-name">{text(record.cargoName) || "未命名貨物"}</div><div className="dmc-code"><span>{text(record.contentId || record.cargoNumber || record.cargoId)}</span>{record.contentIdUpdatedAt ? <span className="dmc-code-updated">已更新</span> : null}</div></div></div>
                    <div className="dmc-head-right"><div className="dmc-quantity">{record.quantity ?? "—"} {text(record.unit) || "件"}</div><span className="dmc-status">{record.assignmentStatus === "UNASSIGNED" ? "尚未派單" : "已派單"}</span><div className="dmc-actions"><button type="button" className="dmc-action" onClick={() => beginEdit(record)} disabled={bulkDeleting}>編輯</button><button type="button" className="dmc-action dmc-action-danger" onClick={() => deleteCargo(record)} disabled={deletingId === record.cargoId || bulkDeleting}>{deletingId === record.cargoId ? "刪除中" : "刪除"}</button></div></div>
                  </div>
                  <CargoPartiesSummary record={record} />
                  {text(record.note) ? <div className="dmc-note">備註：{text(record.note)}</div> : null}
                </article>
              ))}
            </div>
          ) : null}
        </section>

        {modalOpen ? (
          <div className="dmc-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeModal(); }}>
            <form className="dmc-modal" role="dialog" aria-modal="true" aria-labelledby="dmc-modal-title" onSubmit={saveCargo}>
              <div className="dmc-modal-head"><div><h2 id="dmc-modal-title">{editingRecord ? "編輯貨物" : "新增貨物"}</h2></div><button type="button" className="dmc-modal-close" onClick={closeModal} aria-label="關閉" disabled={saving}>×</button></div>
              <div className="dmc-modal-body">
                <section className="dmc-form-section">
                  <div className="dmc-section-head"><strong>貨物資料</strong><span className="dmc-section-caption">編號保存後自動產生</span></div>
                  <div className="dmc-fields">
                    <div className="dmc-field dmc-field-wide"><label htmlFor="dmc-cargo-name">貨物名稱 <span className="dmc-required">*</span></label><input id="dmc-cargo-name" className="dmc-input" value={form.cargoName} onChange={(event) => changeField("cargoName", event.target.value)} maxLength={120} placeholder="例如：電子零件、水果" autoFocus /></div>
                    <div className="dmc-field dmc-field-wide"><label>數量與單位 <span className="dmc-required">*</span></label><div className="dmc-inline-fields"><input className="dmc-input" type="number" min="0.01" step="any" value={form.quantity} onChange={(event) => changeField("quantity", event.target.value)} aria-label="貨物數量" /><input className="dmc-input" value={form.unit} onChange={(event) => changeField("unit", event.target.value)} maxLength={20} placeholder="件" aria-label="數量單位" /></div></div>
                    <div className="dmc-field dmc-field-wide"><label htmlFor="dmc-cargo-note">貨物備註</label><textarea id="dmc-cargo-note" className="dmc-textarea" value={form.note} onChange={(event) => changeField("note", event.target.value)} maxLength={500} placeholder="選填" /></div>
                  </div>
                </section>
                <section className="dmc-form-section">
                  <div className="dmc-section-head dmc-contact-section-head">
                    <div><strong>聯絡人角色</strong></div>
                    <button type="button" className="dmc-contact-picker-button" onClick={openCommonContactPicker} disabled={!orgId || profileLoading || saving}>選擇常用聯絡人</button>
                  </div>
                  <div className="dmc-role-toggle" role="group" aria-label="選擇貨物聯絡人角色">
                    <button type="button" className={`dmc-role-option ${form.contactRole === "SENDER" ? "is-active" : ""}`} aria-pressed={form.contactRole === "SENDER"} onClick={() => changeContactRole("SENDER")}>寄件人</button>
                    <button type="button" className={`dmc-role-option ${form.contactRole === "RECIPIENT" ? "is-active" : ""}`} aria-pressed={form.contactRole === "RECIPIENT"} onClick={() => changeContactRole("RECIPIENT")}>收件人</button>
                  </div>
                  {form.contactRole === "RECIPIENT" ? (
                    <div className="dmc-fields">
                      <div className="dmc-field"><label htmlFor="dmc-recipient-name">收件人姓名 <span className="dmc-required">*</span></label><input id="dmc-recipient-name" className="dmc-input" value={form.recipient.name} onChange={(event) => changeParty("recipient", "name", event.target.value)} maxLength={120} placeholder="收件人姓名" /></div>
                      <div className="dmc-field"><label htmlFor="dmc-recipient-company">公司名稱</label><input id="dmc-recipient-company" className="dmc-input" value={form.recipient.company} onChange={(event) => changeParty("recipient", "company", event.target.value)} maxLength={120} placeholder="選填" /></div>
                      <div className="dmc-field"><label htmlFor="dmc-recipient-phone">聯絡電話</label><input id="dmc-recipient-phone" className="dmc-input" type="tel" value={form.recipient.phone} onChange={(event) => changeParty("recipient", "phone", event.target.value)} maxLength={40} placeholder="選填" /></div>
                      <div className="dmc-field"><label htmlFor="dmc-recipient-email">Email</label><input id="dmc-recipient-email" className="dmc-input" type="email" value={form.recipient.email} onChange={(event) => changeParty("recipient", "email", event.target.value)} maxLength={254} placeholder="選填" autoComplete="email" /></div>
                      <div className="dmc-field dmc-field-wide"><label htmlFor="dmc-recipient-address">送貨地址 <span className="dmc-required">*</span></label><input id="dmc-recipient-address" className="dmc-input" value={form.recipient.addressText} onChange={(event) => changeParty("recipient", "addressText", event.target.value)} maxLength={250} placeholder="輸入收件／送貨地址" /></div>
                    </div>
                  ) : (
                    <div className="dmc-fields">
                      <div className="dmc-field"><label htmlFor="dmc-sender-name">寄件人姓名 <span className="dmc-required">*</span></label><input id="dmc-sender-name" className="dmc-input" value={form.sender.name} onChange={(event) => changeParty("sender", "name", event.target.value)} maxLength={120} placeholder="寄件人姓名" /></div>
                      <div className="dmc-field"><label htmlFor="dmc-sender-company">公司名稱</label><input id="dmc-sender-company" className="dmc-input" value={form.sender.company} onChange={(event) => changeParty("sender", "company", event.target.value)} maxLength={120} placeholder="選填" /></div>
                      <div className="dmc-field"><label htmlFor="dmc-sender-phone">聯絡電話</label><input id="dmc-sender-phone" className="dmc-input" type="tel" value={form.sender.phone} onChange={(event) => changeParty("sender", "phone", event.target.value)} maxLength={40} placeholder="選填" /></div>
                      <div className="dmc-field"><label htmlFor="dmc-sender-email">Email</label><input id="dmc-sender-email" className="dmc-input" type="email" value={form.sender.email} onChange={(event) => changeParty("sender", "email", event.target.value)} maxLength={254} placeholder="選填" autoComplete="email" /></div>
                      <div className="dmc-field dmc-field-wide"><label htmlFor="dmc-sender-address">取貨地址 <span className="dmc-required">*</span></label><input id="dmc-sender-address" className="dmc-input" value={form.sender.addressText} onChange={(event) => changeParty("sender", "addressText", event.target.value)} maxLength={250} placeholder="輸入寄件／取貨地址" /></div>
                    </div>
                  )}
                </section>
              </div>
              {formError ? <p className="dmc-form-error" role="alert">{formError}</p> : null}
              <div className="dmc-modal-foot"><div className="dmc-foot-hint">資料會依每份 700,000 bytes 的安全門檻分片保存。</div><div className="dmc-heading-actions"><button type="button" className="dmc-button" onClick={closeModal} disabled={saving}>取消</button><button type="submit" className="dmc-button dmc-button-primary" disabled={saving}>{saving ? "保存中…" : editingRecord ? "儲存變更" : "建立貨物"}</button></div></div>
            </form>
          </div>
        ) : null}

        {contactPickerOpen ? (
          <div className="dmc-modal-backdrop dmc-contact-picker-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setContactPickerOpen(false); }}>
            <div className="dmc-modal dmc-contact-picker" role="dialog" aria-modal="true" aria-labelledby="dmc-contact-picker-title" onMouseDown={(event) => event.stopPropagation()}>
              <div className="dmc-modal-head">
                <div>
                  <h2 id="dmc-contact-picker-title">選擇常用{form.contactRole === "RECIPIENT" ? "收件人" : "寄件人"}</h2>
                  <p>選取後會填入目前角色的姓名、公司、Email、電話與地址。</p>
                </div>
                <button type="button" className="dmc-modal-close" onClick={() => setContactPickerOpen(false)} aria-label="關閉">×</button>
              </div>
              <label className="dmc-contact-search">
                <span aria-hidden="true">⌕</span>
                <input value={commonSearch} onChange={(event) => setCommonSearch(event.target.value)} placeholder="搜尋姓名、公司、Email、電話或地址" autoFocus />
              </label>
              {commonLoading && !commonContacts.length ? (
                <div className="dmc-contact-empty">正在載入常用聯絡人…</div>
              ) : commonError ? (
                <div className="dmc-contact-empty is-error">{commonError}<button type="button" className="dmc-action" onClick={() => loadCommonContactPage(commonContactPageRef.current + 1)}>重試</button></div>
              ) : !filteredCommonContacts.length ? (
                <div className="dmc-contact-empty">
                  目前沒有符合條件的常用聯絡人。
                  {commonContactHasMoreRef.current ? <button type="button" className="dmc-contact-load-more" onClick={() => loadCommonContactPage()} disabled={commonLoading}>{commonLoading ? "載入中…" : "載入更多聯絡人"}</button> : null}
                </div>
              ) : (
                <div className="dmc-contact-list" onScroll={(event) => {
                  const target = event.currentTarget;
                  if (target.scrollTop + target.clientHeight >= target.scrollHeight - 72) loadCommonContactPage();
                }}>
                  {filteredCommonContacts.map((item) => (
                    <button type="button" className="dmc-contact-item" key={item.id} onClick={() => applyCommonContact(item)}>
                      <span className="dmc-contact-item-main">
                        <strong>{text(item.name || item.displayName) || "未命名聯絡人"}</strong>
                        <small>{[item.company || item.companyName || item.organization, item.email, item.phone || item.tel || item.mobile, item.addressText || item.address || item.fullAddress || item.formattedAddress].map(text).filter(Boolean).join("　·　") || "尚無公司、Email、電話或地址資料"}</small>
                      </span>
                      <span className="dmc-contact-item-action">套用</span>
                    </button>
                  ))}
                  {commonContactHasMoreRef.current ? (
                    <button type="button" className="dmc-contact-load-more" onClick={() => loadCommonContactPage()} disabled={commonLoading}>{commonLoading ? "載入中…" : "載入更多聯絡人"}</button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {workReview ? (
          <div className="dmc-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setWorkReview(null); }}>
            <div className="dmc-modal dmc-work-modal" role="dialog" aria-modal="true" aria-labelledby="dmc-work-review-title">
              <div className="dmc-modal-head">
                <div>
                  <h2 id="dmc-work-review-title">{workReview.mode === "single" ? "單點工作站點確認" : "多點工作站點確認"}</h2>
                </div>
                <button type="button" className="dmc-modal-close" onClick={() => setWorkReview(null)} aria-label="關閉">×</button>
              </div>
              <div className="dmc-modal-body">
                <div className="dmc-preview-stations">
                  {workReview.groups.map((group, index) => {
                    const cargoCount = group.contacts.reduce((sum, contact) => sum + contact.cargos.length, 0);
                    const roleLabel = group.operationType === "PICKUP" ? "取貨站" : "送貨站";
                    return (
                      <article className="dmc-preview-station" key={group.id}>
                        <div className="dmc-preview-station-head">
                          <div className="dmc-preview-station-title">
                            <div><strong>{roleLabel} {index + 1}</strong><small>{group.contacts.length} 位聯絡人・{cargoCount} 筆貨物</small></div>
                          </div>
                        </div>
                        <label className="dmc-preview-address"><span>站點地址</span><input className="dmc-input" value={group.addressText} onChange={(event) => updateReviewGroupAddress(group.id, event.target.value)} maxLength={250} aria-label={`${roleLabel} ${index + 1} 地址`} /></label>
                        <div className="dmc-preview-contacts">
                          {group.contacts.map((contact) => (
                            <div className="dmc-preview-contact" key={contact.id}>
                              <strong>{contact.name || "未命名聯絡人"}</strong>
                              <span>{[contact.company, contact.email, contact.phone].filter(Boolean).join("・") || "尚無公司／Email／電話資料"}</span>
                              <ul>{contact.cargos.map((record) => <li key={record.cargoId}>{text(record.cargoName) || "未命名貨物"}・{record.quantity ?? "—"} {text(record.unit) || "件"}・{text(record.cargoId)}</li>)}</ul>
                            </div>
                          ))}
                        </div>
                      </article>
                    );
                  })}
                </div>
                {workReview.mode === "single" && !reviewSingleValid ? <p className="dmc-work-plan-error">單點工作只能有一個取貨站與一個送貨站；若地址不同，請改用多點工作。</p> : null}
                {workReview.mode === "multi" && !reviewMultiValid ? <p className="dmc-work-plan-error">多點工作需要至少兩個不同的送貨地址；若只有一個送貨地址，請改用單點工作。</p> : null}
                {!reviewAddressValid ? <p className="dmc-work-plan-error">已有聯絡人資料的站點需填寫地址；缺少端點資料可在既有建單表單補齊。</p> : null}
                {!reviewCargoCoverageValid ? <p className="dmc-work-plan-error">有勾選貨物未包含於目前站點分組，請檢查站點與貨物是否完整。</p> : null}
                {workPlanError ? <p className="dmc-work-plan-error" role="alert">{workPlanError}</p> : null}
              </div>
              <div className="dmc-modal-foot">
                <div className="dmc-heading-actions dmc-work-review-actions">
                  <button type="button" className="dmc-button" onClick={() => setWorkReview(null)}>取消</button>
                  <button type="button" className="dmc-button dmc-button-primary" onClick={createWorkFromReview} disabled={!reviewCanCreate}>帶入工作</button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
